import React from "react";
import { SECRET_LETTERS, SECRET_INDEX_BY_STEP, SECRET_PHRASE } from "../../utils/tourUtils";
import { Modal } from "../ui/Modal";
import { colors, radius } from "../../utils/designSystem";

export function AnswerTrackerModal({ open, onClose, tour, progress, justSolvedId }) {
  if (!open) return null;

  const TARGET_COLUMN = 6;

  const rows = tour.steps.map((s, idx) => {
    const solved = Array.isArray(s.acceptedAnswers) &&
      s.acceptedAnswers.includes(progress.answers?.[s.id]);

    const display = (s.solution || s.acceptedAnswers?.[0] || "")
      .toString()
      .toUpperCase()
      .trim();

    const letters = display.replace(/\s+/g, "").split("");
    const answerLetterIndex = (typeof s.secretIndex === "number")
      ? s.secretIndex
      : (SECRET_INDEX_BY_STEP[s.id] ?? 0);

    const leftPad = Math.max(0, TARGET_COLUMN - answerLetterIndex);
    const totalBoxes = leftPad + letters.length;
    const highlightCol = leftPad + answerLetterIndex;

    return { idx, id: s.id, solved, letters, leftPad, totalBoxes, highlightCol };
  });

  const solvedCount = rows.filter(r => r.solved).length;

  const BASE_BOX =
    "box-border grid h-7 w-7 sm:h-8 sm:w-8 place-items-center rounded-sm shrink-0 " +
    "text-sm font-semibold leading-none";

  return (
    <Modal open={open} onClose={onClose} showDefaultAction={false}>
      <div className="relative pt-2" style={{ WebkitTextSizeAdjust: "100%" }}>
        {}
        <div className="mb-3 pr-24">
          <div className="text-2xl font-extrabold" style={{ color: colors.neutral[900] }}>
            Answer Tracker
          </div>
          <div className="text-sm mt-1" style={{ color: colors.neutral[500] }}>
            {solvedCount} of {tour.steps.length} locations solved
          </div>
        </div>

        {}
        <button
          type="button"
          aria-label="Close tracker"
          onClick={onClose}
          className="absolute right-3 top-3 px-4 py-2 rounded-full border transition-all text-sm font-semibold shadow-sm"
          style={{
            borderColor: colors.neutral[300],
            background: colors.neutral.white,
            color: colors.neutral[700]
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = colors.neutral[50];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = colors.neutral.white;
          }}
        >
          Close
        </button>

        <div className="mb-4 text-sm" style={{ color: colors.neutral[600] }}>
          The <span style={{ color: colors.treasure.gold, fontWeight: 600 }}>★ gold box</span> on every row reveals one letter; together they spell a final clue.
        </div>

        <div
          className="space-y-3 overflow-y-auto pr-1"
          style={{ maxHeight: "65svh", WebkitOverflowScrolling: "touch" }}
        >
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-2 flex-nowrap">
              <span
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all"
                style={{
                  background: r.solved ? colors.success.main : colors.neutral[200],
                  color: r.solved ? colors.neutral.white : colors.neutral[600],
                  border: r.solved ? `2px solid ${colors.success.dark}` : `2px solid ${colors.neutral[300]}`
                }}
                title={`Location ${r.idx + 1}${r.solved ? ' - Solved!' : ''}`}
              >
                {r.solved ? '✓' : r.idx + 1}
              </span>

              {}
              <div className="grid grid-flow-col auto-cols-[28px] gap-1 overflow-x-auto min-w-0" style={{ WebkitOverflowScrolling: "touch" }}>
                {}
                {Array.from({ length: r.leftPad }).map((_, i) => (
                  <div
                    key={`pad-${i}`}
                    className={`${BASE_BOX} border border-transparent bg-transparent`}
                    aria-hidden="true"
                  />
                ))}

                {}
                {r.letters.map((ch, i) => {
                  const absoluteCol = r.leftPad + i;
                  const isSecret = absoluteCol === r.highlightCol;
                  const anim = (r.id === justSolvedId && r.solved) ? " fade-in-letter" : "";
                  const className = `${BASE_BOX} border ${anim}`;
                  const style = Object.assign(
                    {},
                    isSecret
                      ? {
                          background: r.solved ? 'linear-gradient(135deg, #F1C40F 0%, #F39C12 100%)' : colors.neutral[100],
                          borderColor: r.solved ? colors.treasure.amber : colors.neutral[300],
                          borderWidth: '2px',
                          borderRadius: radius.md,
                          color: r.solved ? colors.neutral[900] : colors.neutral[500],
                          boxShadow: r.solved ? '0 2px 8px rgba(241, 196, 15, 0.3)' : 'none'
                        }
                      : {
                          background: colors.neutral.white,
                          borderColor: colors.neutral[300],
                          borderRadius: radius.sm,
                          color: colors.neutral[700]
                        },
                    (r.id === justSolvedId && r.solved) ? { animationDelay: (i * 60) + "ms" } : {}
                  );
                  return (
                    <div key={i} className={className} style={style}>
                      {r.solved ? ch : ""}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
                  {}
          <div
            className="sticky bottom-0 -mx-1 mt-4 rounded-lg backdrop-blur p-4 text-center"
            style={{
              background: 'linear-gradient(to bottom, rgba(255,255,255,0.85), rgba(255,255,255,0.95))',
              border: `1px solid ${colors.neutral[200]}`,
              boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)'
            }}
          >
            <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: colors.neutral[500] }}>
              Final Clue ({solvedCount}/{SECRET_LETTERS.length})
            </div>
            <div className="text-lg font-bold tracking-wide" style={{ color: colors.neutral[900], letterSpacing: '0.15em' }}>
              {(() => {
                let li = 0;
                return Array.from(SECRET_PHRASE).map((ch, i) => {
                  if (ch === " ") {
                    return <span key={"sp"+i} className="inline-block w-3 sm:w-4"></span>;
                  }

                  const show = !!rows[li]?.solved; li++;
                  return (
                    <span
                      key={i}
                      className="inline-block w-5 text-center transition-all duration-300"
                      style={{
                        color: show ? colors.treasure.gold : colors.neutral[300]
                      }}
                    >
                      {show ? ch : '•'}
                    </span>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
